// Manager (ASM) — Team TA/DA claims dashboard. Total / Approved / Rejected
// claim amounts per reportee, click a row to see that user's individual
// claims. Backed by GET /tada/team-summary + GET /tada/approval?user_id=
// (both already visibility-scoped to the caller's team).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, CheckCircle2, XCircle, ChevronDown, ChevronRight, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

function _monthBounds(month) {
  // "YYYY-MM" -> [first day, last day] of that IST month, for date_from/
  // date_to on /tada/approval (which filters on claim_date, a plain date
  // string — no timezone math needed beyond the calendar itself).
  if (!month) return [null, null];
  const [y, m] = month.split("-").map(Number);
  const first = `${month}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const last = `${month}-${String(lastDay).padStart(2, "0")}`;
  return [first, last];
}

const STATUS_BADGE = {
  submitted:        "bg-amber-100 text-amber-800 border-amber-200",
  manager_approved: "bg-sky-100 text-sky-800 border-sky-200",
  admin_approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:         "bg-rose-100 text-rose-800 border-rose-200",
  draft:            "bg-neutral-100 text-neutral-700 border-neutral-200",
};

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-neutral-50/80 border border-neutral-100 px-2.5 py-2">
      <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
           style={{ backgroundColor: `${accent}1a`, color: accent }}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-neutral-900 leading-tight truncate">₹{value}</div>
        <div className="text-[9px] uppercase tracking-wider text-neutral-400 leading-tight">{label}</div>
      </div>
    </div>
  );
}

function UserClaimsDetail({ userId, month }) {
  const [claims, setClaims] = useState(null);
  useEffect(() => {
    setClaims(null);
    const [date_from, date_to] = _monthBounds(month);
    const params = { user_id: userId, statuses: "all", page_size: 50 };
    if (date_from) { params.date_from = date_from; params.date_to = date_to; }
    api.get("/tada/approval", { params })
      .then(({ data }) => setClaims(data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [userId, month]);

  if (!claims) {
    return <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-neutral-400" /></div>;
  }
  if (claims.length === 0) {
    return <div className="text-xs text-neutral-400 py-3 text-center">No claims found.</div>;
  }
  return (
    <div className="px-3 pb-3 pt-1 space-y-1.5">
      {claims.map((c) => (
        <div key={c.id} className="flex items-center justify-between rounded-lg bg-white border border-neutral-100 px-3 py-2"
             data-testid={`team-claims-detail-row-${c.id.slice(0, 8)}`}>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-neutral-900">{c.claim_date}</div>
            <Badge className={"border text-[10px] mt-0.5 " + (STATUS_BADGE[c.status] || "")}>
              {(c.status || "").replace("_", " ")}
            </Badge>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs font-bold text-neutral-900">₹{c.total_claimed_inr}</div>
            <div className="text-[10px] text-neutral-500">approved ₹{c.total_approved_inr}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const _todayIso = () => new Date().toISOString().slice(0, 10);
const _thisMonth = () => _todayIso().slice(0, 7);

export default function TeamClaimsSummary() {
  const [team, setTeam] = useState(null);
  const [totals, setTotals] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [month, setMonth] = useState(_thisMonth());
  const [userQuery, setUserQuery] = useState("");

  useEffect(() => {
    setTeam(null);
    api.get("/tada/team-summary", { params: month ? { month } : {} })
      .then(({ data }) => { setTeam(data.team || []); setTotals(data.totals || null); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [month]);

  const initials = (name) => (name || "?")
    .split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const visibleTeam = (team || []).filter((t) =>
    (t.name || "").toLowerCase().includes(userQuery.trim().toLowerCase()));

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="team-claims-summary-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Manager view</div>
        <h1 className="text-2xl font-extrabold text-neutral-900">Team claims</h1>
        <p className="text-xs text-neutral-500 mt-0.5">
          TA/DA claim amounts{month ? "" : ", all-time"} — tap a name for details.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input type="month" value={month} max={_thisMonth()}
               onChange={(e) => setMonth(e.target.value)}
               className="h-9 max-w-[170px]" data-testid="team-claims-month-filter" />
        {month && (
          <button type="button" onClick={() => setMonth("")}
                  className="text-xs font-semibold text-[#6D2C91]"
                  data-testid="team-claims-month-clear">
            All time
          </button>
        )}
        <div className="relative flex-1 min-w-[140px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <Input value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                 placeholder="Search user…" className="h-9 pl-8"
                 data-testid="team-claims-user-filter" />
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-1.5" data-testid="team-claims-totals">
          <StatTile icon={Wallet} label="Total" value={totals.claimed_inr} accent="#6D2C91" />
          <StatTile icon={CheckCircle2} label="Approved" value={totals.approved_inr} accent="#059669" />
          <StatTile icon={XCircle} label="Rejected" value={totals.rejected_inr} accent="#E11D48" />
        </div>
      )}

      {!team ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
      ) : visibleTeam.length === 0 ? (
        <div className="py-10 text-center text-neutral-400 text-sm">
          {team.length === 0 ? "No team members visible." : "No user matches your search."}
        </div>
      ) : (
        <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1 -mr-1" data-testid="team-claims-list">
          {visibleTeam.map((t) => {
            const isOpen = expandedId === t.user_id;
            return (
              <Card key={t.user_id} className="border-neutral-200 rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-3 text-left"
                    onClick={() => setExpandedId((id) => id === t.user_id ? null : t.user_id)}
                    data-testid={`team-claims-row-${t.user_id.slice(0, 8)}`}>
                    <div className="h-9 w-9 rounded-full tulsi-gradient text-white flex items-center justify-center text-[11px] font-bold shrink-0">
                      {initials(t.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-neutral-900 truncate">{t.name || "—"}</div>
                      <div className="text-[10px] text-neutral-400">{t.claim_count} claim(s)</div>
                    </div>
                    <div className="text-right shrink-0 pl-1">
                      <div className="text-xs font-bold text-neutral-900">₹{t.claimed_inr}</div>
                      <div className="text-[10px] text-emerald-600">₹{t.approved_inr} approved</div>
                    </div>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-400 shrink-0" />
                             : <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />}
                  </button>
                  {isOpen && <UserClaimsDetail userId={t.user_id} month={month} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
